import {
  Badge,
  Box,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconSchool } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Printable } from "@components";
import { AXIS_KEYS, AxisKey } from "@utils/cognitiveProfile";
import { CognitiveAxisCard } from "./components/CognitiveAxisCard";
import { CognitiveDisclaimerBanner } from "./components/CognitiveDisclaimerBanner";
import { CognitiveProgramRecommendations } from "./components/CognitiveProgramRecommendations";
import { CognitiveRadarChart } from "./components/CognitiveRadarChart";
import { CognitiveTopProfessionsCard } from "./components/CognitiveTopProfessionsCard";
import { PatientHeader } from "./components/PatientHeader";
import { CognitiveProfileTestIds, useCognitiveProfileData } from "./hooks/useCognitiveProfileData";
import { useCognitiveColor } from "./hooks/useCognitiveFillVariant";

export interface CognitiveProfileOverviewProps {
  testIds?: CognitiveProfileTestIds;
}

export default function CognitiveProfileOverview({ testIds }: CognitiveProfileOverviewProps = {}) {
  const { t } = useTranslation();
  const colorFor = useCognitiveColor();
  const { patientData, axes, topMatches, topStrengths, missingTests, perTestDetail, loading } =
    useCognitiveProfileData(testIds);

  const hasAnyData = perTestDetail.length > 0;
  const detailByAxis = new Map(perTestDetail.map((d) => [d.axis, d] as const));
  const topStrengthRankByAxis = new Map<AxisKey, number>(
    topStrengths.map((s, idx) => [s.key, idx + 1] as const),
  );

  return (
    <Printable filename="cognitive-profile.pdf">
      <Printable.Section breakRule="avoid">
        <Box>
          <LoadingOverlay
            visible={loading}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />

          <PatientHeader
            patientData={patientData}
            icon={<IconSchool size={24} color="white" />}
            title={t("cognitiveProfile.title")}
            subtitle={t("cognitiveProfile.subtitle")}
            overallStatus={{
              label: t("cognitiveProfile.headerStatusLabel"),
              color: "blue",
            }}
          />

          <CognitiveDisclaimerBanner />

          {hasAnyData ? (
            <Grid gutter="md" mb="md" align="stretch" className="print-row-2">
              <Grid.Col span={{ base: 12, lg: 5 }}>
                <Paper p="md" radius="md" withBorder shadow="md" h="100%">
                  <Stack gap={2} mb="xs">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts={1.2}>
                      {t("cognitiveProfile.student.eyebrow")}
                    </Text>
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Title order={4}>{t("cognitiveProfile.student.heading")}</Title>
                      <Badge size="sm" variant="filled" color={colorFor("blue")} tt="none">
                        {t("cognitiveProfile.student.completedCount", {
                          count: perTestDetail.length,
                          total: AXIS_KEYS.length,
                        })}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {t("cognitiveProfile.student.intro")}
                    </Text>
                  </Stack>
                  <CognitiveRadarChart axes={axes} height={320} />
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 7 }}>
                <CognitiveTopProfessionsCard matches={topMatches} />
              </Grid.Col>
            </Grid>
          ) : (
            <Paper p="xl" radius="md" mb="md" withBorder shadow="md">
              <Stack gap={4} mb="md">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts={1.2}>
                  {t("cognitiveProfile.student.eyebrow")}
                </Text>
                <Title order={3}>{t("cognitiveProfile.student.heading")}</Title>
              </Stack>
              <Paper p="md" radius="md" withBorder bg="var(--oculera-surface-alt)">
                <Text size="sm" c="dimmed">
                  {t("cognitiveProfile.student.noData")}
                </Text>
              </Paper>
            </Paper>
          )}

          {hasAnyData && (
            <Paper p="lg" radius="md" mb="md" withBorder shadow="md">
              <Stack gap={4} mb="md">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts={1.2}>
                  {t("cognitiveProfile.counsellor.eyebrow")}
                </Text>
                <Title order={3}>{t("cognitiveProfile.axisCard.sectionHeading")}</Title>
                <Text size="sm" c="dimmed">
                  {t("cognitiveProfile.axisCard.sectionIntro")}
                </Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="md" className="print-cols-3">
                {AXIS_KEYS.map((key) => (
                  <CognitiveAxisCard
                    key={key}
                    axisKey={key}
                    detail={detailByAxis.get(key)}
                    isTopStrength={topStrengthRankByAxis.has(key)}
                    topStrengthRank={topStrengthRankByAxis.get(key)}
                  />
                ))}
              </SimpleGrid>
            </Paper>
          )}

          {missingTests.length > 0 && (
            <Paper p="lg" radius="md" mb="md" withBorder>
              <Stack gap="sm">
                <Title order={5}>{t("cognitiveProfile.counsellor.missingTestsLabel")}</Title>
                <Group gap="xs">
                  {missingTests.map((c) => (
                    <Badge key={c} color={colorFor("gray")} variant="filled" tt="none">
                      {t(c, { defaultValue: c })}
                    </Badge>
                  ))}
                </Group>
                <Text size="xs" c="dimmed">
                  {t("cognitiveProfile.counsellor.missingTestsHint")}
                </Text>
              </Stack>
            </Paper>
          )}

          {hasAnyData && (
            <Text size="sm" c="dimmed" ta="center" mt="md">
              {t("cognitiveProfile.student.exploringPrompt")}
            </Text>
          )}

          <Text size="xs" c="dimmed" ta="center" mt="md" mb="md">
            {t("cognitiveProfile.footer")}
          </Text>
        </Box>
      </Printable.Section>

      {hasAnyData && (
        <Printable.Section breakRule="avoid">
          <Box>
            <CognitiveProgramRecommendations matches={topMatches} />

            <Text size="xs" c="dimmed" ta="center" mt="md" mb="md">
              {t("cognitiveProfile.footer")}
            </Text>
          </Box>
        </Printable.Section>
      )}
    </Printable>
  );
}
